import type {TicketChannel} from '../types/ticket';

// Deliberately dependency-free: this module is the one place a channel is named, so
// anything that needs to name one — a detail header, a grouping util — must be able
// to import it without pulling the application in behind it. The one channel helper
// that needs runtime feature state, `selectableChannels`, lives in its own module
// for that reason.

/**
 * How a ticket's arrival channel reads to an agent. Named once so the detail
 * header, the Details panel and any filter can never disagree, and so the raw
 * enum value (`customer_portal`) never reaches the page.
 *
 * This map is intentionally complete and unconditional: a ticket that was raised
 * through the portal carries `channel = 'customer_portal'` in the database
 * forever, and must always render a human-readable label regardless of whether
 * the current firm has the workspace enabled.
 */
export const CHANNEL_LABELS: Record<TicketChannel, string> = {
	manual: 'Manual',
	email: 'Email',
	phone: 'Phone',
	chat: 'Chat',
	internal: 'Internal',
	api: 'API',
	customer_portal: 'Client Workspace',
	direct: 'Direct',
};

/**
 * The design system glyph for a channel, where one fits. Email is the only channel
 * with an unambiguous glyph in the set, and it is also the one that matters — an
 * emailed ticket has a customer waiting on a reply. The rest read as text alone
 * rather than borrowing a glyph that means something else.
 */
export const CHANNEL_ICONS: Partial<Record<TicketChannel, string>> = {
	email: 'mail',
};
